// Web CRM (Business plane) language context — Web-only interface localization.
// Persists the selected UI language in localStorage['crm_lang']. Default + fallback = English.
// This ONLY affects static UI chrome; it never transforms backend/user-entered content.

import React, { createContext, useContext, useCallback, useEffect, useMemo, useState } from 'react';
import {
  t as translate,
  isSupportedLanguage,
  SUPPORTED_LANGUAGES,
  LANGUAGE_OPTIONS,
  RTL_LANGUAGES,
} from '../i18n/translations';

const STORAGE_KEY = 'crm_lang';
const DEFAULT_LANG = 'en';

function readInitialLang() {
  try {
    const stored = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
    if (stored && isSupportedLanguage(stored)) return stored;
  } catch (e) {
    // localStorage unavailable (private mode / SSR) — fall back to English.
  }
  return DEFAULT_LANG;
}

const LanguageContext = createContext({
  language: DEFAULT_LANG,
  setLanguage: () => {},
  t: (key, params) => translate(key, DEFAULT_LANG, params),
  options: LANGUAGE_OPTIONS,
  supported: SUPPORTED_LANGUAGES,
  isRTL: false,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(readInitialLang);

  const setLanguage = useCallback((lang) => {
    if (!isSupportedLanguage(lang)) return; // guard: never store an unsupported/fake code
    setLanguageState(lang);
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // ignore persistence failure; in-memory language still applies for this session
    }
  }, []);

  // Reflect language + direction on <html> for the current session (static labels only).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const rtl = RTL_LANGUAGES.includes(language);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.setAttribute('dir', rtl ? 'rtl' : 'ltr');
  }, [language]);

  const t = useCallback((key, params) => translate(key, language, params), [language]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      options: LANGUAGE_OPTIONS,
      supported: SUPPORTED_LANGUAGES,
      isRTL: RTL_LANGUAGES.includes(language),
    }),
    [language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}

// Convenience hook mirroring the mobile useTranslation()/useT() ergonomics.
export function useTranslation() {
  const { t, language, setLanguage, options } = useContext(LanguageContext);
  return { t, language, setLanguage, options };
}

export function useT() {
  return useContext(LanguageContext).t;
}

export default LanguageContext;
